/**
 * Query key factory. Store-scoped lists include the active store scope so switching stores
 * naturally loads a separate cache entry (and refetches). Record-level keys are not scoped:
 * the backend authorizes each record request.
 */
export type StoreScope = string; // storeId or "all"

export const qk = {
  me: () => ["session", "me"] as const,
  organizations: () => ["session", "organizations"] as const,

  stores: {
    list: () => ["stores", "list"] as const,
    detail: (storeId: string) => ["stores", "detail", storeId] as const,
    members: (storeId: string) => ["stores", "members", storeId] as const,
  },

  overview: (scope: StoreScope, range: string) => ["overview", scope, range] as const,

  opportunities: {
    all: ["opportunities"] as const,
    lists: () => ["opportunities", "list"] as const,
    list: (scope: StoreScope, filters: object) => ["opportunities", "list", scope, filters] as const,
    detail: (id: string) => ["opportunities", "detail", id] as const,
    activity: (id: string) => ["opportunities", "activity", id] as const,
    offers: (id: string) => ["opportunities", "offers", id] as const,
    valuations: (id: string) => ["opportunities", "valuations", id] as const,
    appointments: (id: string) => ["opportunities", "appointments", id] as const,
    followups: (id: string) => ["opportunities", "followups", id] as const,
    notes: (id: string) => ["opportunities", "notes", id] as const,
    images: (id: string) => ["opportunities", "images", id] as const,
  },

  conversations: {
    lists: () => ["conversations", "list"] as const,
    list: (scope: StoreScope, filters: object) => ["conversations", "list", scope, filters] as const,
    detail: (id: string) => ["conversations", "detail", id] as const,
    messages: (id: string) => ["conversations", "messages", id] as const,
    runs: (id: string) => ["conversations", "runs", id] as const,
    media: (messageId: string, index: number) => ["conversations", "media", messageId, index] as const,
  },

  appointments: {
    lists: () => ["appointments", "list"] as const,
    list: (scope: StoreScope, filters: object) => ["appointments", "list", scope, filters] as const,
  },
  purchases: {
    lists: () => ["purchases", "list"] as const,
    list: (scope: StoreScope, filters: object) => ["purchases", "list", scope, filters] as const,
  },
  leadImports: {
    all: ["lead-imports"] as const,
    fields: () => ["lead-imports", "fields"] as const,
    list: (scope: StoreScope, filters: object) => ["lead-imports", "list", scope, filters] as const,
    detail: (id: string) => ["lead-imports", "detail", id] as const,
    rows: (id: string, filters: object) => ["lead-imports", "rows", id, filters] as const,
  },
  apiKeys: (scope: StoreScope) => ["api-keys", scope] as const,
  sources: (scope: StoreScope, range: string) => ["sources", scope, range] as const,
  analytics: (scope: StoreScope, filters: object) => ["analytics", scope, filters] as const,
  search: (scope: StoreScope, q: string) => ["search", scope, q] as const,
  vehicle: (id: string) => ["vehicles", id] as const,
};
